import {
  AdminDollarInput2,
  Box,
  BrandRequestDetailsEntity,
  Button,
  Chevron,
  Disclaimer,
  Div,
  ErrorToast,
  H5,
  Icon,
  Modal,
  P,
  Pill,
  SuccessToast,
  TooltipIcon,
  centsToDollars,
  formatter,
  lightTheme,
  reportError,
} from '@vgrubs/components';
import { ImageInput } from 'components/src/adminPages/Menu/components/ImageInput';
import { Collapsible } from 'components/src/adminPages/Menu/MenyStyled';
import { brandRequestUpdateItem } from 'components/src/api/brands.api';
import {
  BTMenuItemResponse,
  BTModifierGroupItemResponse,
  BTModifierItemResponse,
} from 'components/src/types/brand.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

/**
 * Edit one submitted item before approving the brand: price within the suggested range,
 * image, and every modifier option's price. Saves through the brand request endpoint.
 */
export const EditBrandItemModal = ({
  editItemModal,
  closeModal,
  setLoading,
  brandId,
  setEditItemModal,
  setBrand,
}: {
  editItemModal: BTMenuItemResponse | null;
  closeModal: () => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
  brandId: string | number;
  setEditItemModal: Dispatch<SetStateAction<BTMenuItemResponse | null>>;
  setBrand: Dispatch<SetStateAction<BrandRequestDetailsEntity | null>>;
}) => {
  const item = editItemModal;

  const [price, setPrice] = useState(0);
  const [image, setImage] = useState<string | Blob | File | null>(null);

  useEffect(() => {
    if (item) {
      setPrice(item.chosenPrice ?? 0);
      setImage(item.imageUrl ?? null);
    }
  }, [item]);

  const handleValuePriceItem = useCallback((val) => {
    setPrice(val);
  }, []);

  async function handleUpdateItem() {
    if (price < (item?.fromPrice ?? 0) || price > (item?.toPrice ?? 0)) {
      ErrorToast('Item price must be between suggested price range');
      return;
    }

    if (
      item?.modifierGroups.some((modifierGroup) =>
        modifierGroup.options.some(
          (option) =>
            option.chosenPrice !== null &&
            option.chosenPrice !== undefined &&
            (option.chosenPrice < (option.fromPrice ?? 0) ||
              option.chosenPrice > (option.toPrice ?? 0))
        )
      )
    ) {
      ErrorToast('Option price must be between suggested price range');
      return;
    }

    const formData = new FormData();

    const body = {
      id: item!.id,
      price: price,
      modifierGroups: item!.modifierGroups.map((modifierGroup) => ({
        id: modifierGroup.id,
        options: modifierGroup.options.map((option) => ({
          id: option.id,
          price: option.chosenPrice ?? 0,
        })),
      })),
    };

    formData.append('brand', JSON.stringify(body));

    if (typeof image !== 'string' && image !== null) {
      formData.append('image', image);
    }

    setLoading(true);
    try {
      const { data } = await brandRequestUpdateItem(brandId, formData);
      SuccessToast(data.message);
      closeModal();
      setBrand(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const isValidPrice = useCallback(
    (val: number) => {
      return val >= (item?.fromPrice ?? 0) && val <= (item?.toPrice ?? 0);
    },
    [item?.fromPrice, item?.toPrice]
  );

  const percentage = Number(
    (
      ((price - (item?.fromPrice ?? 0)) /
        ((item?.toPrice ?? 0) - (item?.fromPrice ?? 0))) *
      100
    ).toFixed(2)
  );

  const fitText = () => {
    if (percentage >= 0 && percentage <= 33) {
      return '9-12 Daily Orders Expected';
    }
    if (percentage > 33 && percentage <= 66) {
      return '5-9 Daily Orders Expected';
    }
    if (percentage > 66 && percentage <= 100) {
      return '2-5 Daily Orders Expected';
    }
    return 'Invalid Price';
  };

  const color = () => {
    if (percentage >= 0 && percentage <= 33) {
      return lightTheme.green;
    }
    if (percentage > 33 && percentage <= 66) {
      return lightTheme.chipYellow;
    }
    return lightTheme.primary;
  };

  return (
    <Modal
      isOpen={!!editItemModal}
      closeModal={closeModal}
      logo={false}
      header="Edit Item"
      modalOverflow="visible"
    >
      <Div $flex $column $gap="24px">
        <Div $flex $center>
          <ImageInput
            image={image}
            onChange={(img) => setImage(img)}
            // onDelete={() => setImage(null)}
          />
        </Div>

        <Div $flex $column $gap="8px">
          <AdminDollarInput2
            label="Price"
            value={price}
            onValueChange={handleValuePriceItem}
            $textCenter
            $color={isValidPrice(price) ? lightTheme.green : lightTheme.primary}
          />
          <Div $flex $column $alignCenter $justifyCenter $gap="6px">
            <Disclaimer $center $m="0">
              Suggested price:{' '}
              <span style={{ color: lightTheme.green }}>
                {formatter.format(centsToDollars(item?.fromPrice ?? 0))} -{' '}
                {formatter.format(centsToDollars(item?.toPrice ?? 0))}
              </span>
            </Disclaimer>
            <Div $flex $alignCenter $gap="4px">
              <Pill $background={`${color()}`}>
                <Disclaimer $m="0" $color={lightTheme.white}>
                  {fitText()}
                </Disclaimer>
              </Pill>
              <TooltipIcon tooltip="These are estimated ranges based on similar brands and locations. Actual results may vary." />
            </Div>
          </Div>
        </Div>
        {item?.modifierGroups && item?.modifierGroups.length > 0 && (
          <Div $flex $column $gap="8px">
            {item?.modifierGroups.map((modifierGroup) => (
              <ModifierGroup
                key={modifierGroup.id}
                modifierGroup={modifierGroup}
                setEditItemModal={setEditItemModal}
              />
            ))}
          </Div>
        )}
      </Div>
      <Button $selected $w="100%" onClick={handleUpdateItem} $mt="24">
        Save
      </Button>
    </Modal>
  );
};

const ModifierGroup = ({
  modifierGroup,
  setEditItemModal,
}: {
  modifierGroup: BTModifierGroupItemResponse;
  setEditItemModal: Dispatch<SetStateAction<BTMenuItemResponse | null>>;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const elementHeight = ref?.current?.clientHeight;
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <Box $box $p="8px 20px" $flex $alignCenter $between>
        <H5 $m="0">{modifierGroup.name}</H5>
        <Icon
          $pointer
          onClick={() => setExpanded((prev) => !prev)}
          $rotate={expanded ? '360deg' : '180deg'}
        >
          <Chevron />
        </Icon>
      </Box>
      <Collapsible expanded={expanded} elementHeight={elementHeight}>
        {modifierGroup.options.map((option) => (
          <ModifierGroupOption
            key={option.id}
            option={option}
            setEditItemModal={setEditItemModal}
            modifierGroupId={modifierGroup.id}
          />
        ))}
      </Collapsible>
    </div>
  );
};

const ModifierGroupOption = ({
  option,
  setEditItemModal,
  modifierGroupId,
}: {
  option: BTModifierItemResponse;
  setEditItemModal: Dispatch<SetStateAction<BTMenuItemResponse | null>>;
  modifierGroupId: number;
}) => {
  const handleUpdateOptionPrice = useCallback(
    (val: number) => {
      setEditItemModal((prev: BTMenuItemResponse | null) => {
        if (!prev) return prev;
        return {
          ...prev,
          modifierGroups: prev.modifierGroups.map((modifierGroup) => {
            if (modifierGroup.id !== modifierGroupId) return modifierGroup;
            return {
              ...modifierGroup,
              options: modifierGroup.options.map((opt) =>
                opt.id === option.id ? { ...opt, chosenPrice: val ?? 0 } : opt
              ),
            };
          }),
        };
      });
    },
    [setEditItemModal, modifierGroupId]
  );

  const isValidPrice = useCallback(
    (val: number) => {
      return val >= (option.fromPrice ?? 0) && val <= (option.toPrice ?? 0);
    },
    [option.fromPrice, option.toPrice]
  );

  return (
    <Div $borderBottom $flex $alignCenter $between $p="8px 0px">
      <Div $flex $column $gap="2px">
        <P $m="0" $left>
          {option.name}
        </P>
        <Disclaimer $m="0" $left>
          Suggested price:{' '}
          <span style={{ color: lightTheme.green }}>
            {formatter.format(centsToDollars(option.fromPrice ?? 0))} -{' '}
            {formatter.format(centsToDollars(option.toPrice ?? 0))}
          </span>
        </Disclaimer>
      </Div>
      <Div $width="100px">
        <AdminDollarInput2
          value={option.chosenPrice ?? 0}
          onValueChange={(val) => handleUpdateOptionPrice(val)}
          $textCenter
          $color={
            isValidPrice(option.chosenPrice ?? 0)
              ? lightTheme.green
              : lightTheme.primary
          }
        />
      </Div>
    </Div>
  );
};
